import React, { useState, useEffect } from 'react';
import { cemeteryAPI, importAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const UnderConstructionPage = () => {
    const { user: currentUser } = useAuth();
    const [activeTab, setActiveTab] = useState('import');
    const [cemeteries, setCemeteries] = useState([]);
    const [selectedCemetery, setSelectedCemetery] = useState('');
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState(null);
    const [error, setError] = useState(null);

    // Initial Load
    useEffect(() => {
        if (!currentUser) return;

        const fetchCemeteries = async () => {
            try {
                const response = await cemeteryAPI.list();
                const list = response.data.cemeteries || response.data;
                setCemeteries(list);
                if (list.length > 0) {
                    setSelectedCemetery(list[0].unique_name);
                }
            } catch (err) {
                setError("Failed to load cemeteries");
            }
        };
        fetchCemeteries();
    }, [currentUser]);

    const handleLegacyImport = async (event) => {
        const file = event.target.files[0];
        if (!file || !selectedCemetery) return;

        setLoading(true);
        setMessage(null);
        setError(null);

        try {
            await importAPI.legacyImport(selectedCemetery, file);
            setMessage("Legacy Import V4 Completed Successfully (Check Logs for details)");
        } catch (err) {
            setError(err.response?.data?.detail || "Import Failed");
        } finally {
            setLoading(false);
        }
    };

    const handleLegacyBackup = async () => {
        if (!selectedCemetery) return;

        setLoading(true);
        setMessage(null);
        setError(null);

        try {
            const response = await importAPI.legacyBackup(selectedCemetery);

            // Trigger download
            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/zip' }));
            const link = document.createElement('a');
            link.href = url;
            const contentDisposition = response.headers['content-disposition'];
            let fileName = 'legacy_backup_v4.zip';
            if (contentDisposition) {
                const fileNameMatch = contentDisposition.match(/filename=["']?([^"']+)["']?/);
                if (fileNameMatch && fileNameMatch.length === 2) fileName = fileNameMatch[1];
            }
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();

            setMessage("Legacy Backup V4 Generated & Downloaded");
        } catch (err) {
            setError("Backup Generation Failed");
        } finally {
            setLoading(false);
        }
    };

    const styles = {
        container: {
            padding: '20px',
            maxWidth: '800px',
            margin: '0 auto',
            textAlign: 'center',
            fontFamily: 'sans-serif'
        },
        card: {
            border: '2px dashed #ef6c00', // Darker orange
            padding: '40px',
            borderRadius: '8px',
            backgroundColor: '#ffffff', // White background for better contrast
            color: '#333' // Dark text
        },
        title: {
            color: '#d84315', // Darker reddish-orange
            fontSize: '2rem',
            marginBottom: '10px'
        },
        warningIcon: {
            fontSize: '3rem',
            color: '#ef6c00'
        },
        select: {
            padding: '10px',
            fontSize: '16px',
            marginBottom: '20px',
            width: '50%',
            borderRadius: '4px',
            border: '1px solid #ccc'
        },
        tabs: {
            display: 'flex',
            justifyContent: 'center',
            marginBottom: '20px',
            borderBottom: '1px solid #ccc'
        },
        tab: (isActive) => ({
            padding: '10px 20px',
            cursor: 'pointer',
            borderBottom: isActive ? '3px solid #d84315' : 'none',
            fontWeight: isActive ? 'bold' : 'normal',
            color: isActive ? '#d84315' : '#555'
        }),
        actionArea: {
            border: '1px solid #ddd',
            padding: '20px',
            borderRadius: '8px',
            backgroundColor: '#f9f9f9' // Very light grey instead of white on white
        },
        button: {
            backgroundColor: '#d84315',
            color: 'white',
            padding: '10px 20px',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '16px',
            marginTop: '10px'
        },
        alert: (type) => ({
            padding: '15px',
            margin: '10px 0',
            borderRadius: '4px',
            backgroundColor: type === 'error' ? '#ffebee' : '#e8f5e9',
            color: type === 'error' ? '#b71c1c' : '#1b5e20', // Darker text for alerts
            border: `1px solid ${type === 'error' ? '#ef9a9a' : '#a5d6a7'}`
        })
    };

    if (!currentUser) return <div style={styles.container}>Please log in.</div>;

    return (
        <div style={styles.container}>
            <div style={styles.card}>
                <div style={styles.warningIcon}>⚠️</div>
                <h1 style={styles.title}>Under Construction</h1>
                <p>
                    This area contains <b>Experimental V4 Features</b> for Legacy System Parity.
                    <br />
                    Use with caution. These tools are isolated from the main Logic.
                </p>

                <div style={{ marginTop: '30px' }}>
                    <select
                        style={styles.select}
                        value={selectedCemetery}
                        onChange={(e) => setSelectedCemetery(e.target.value)}
                    >
                        {cemeteries.map(cem => (
                            <option key={cem.id} value={cem.unique_name}>{cem.name}</option>
                        ))}
                    </select>
                </div>

                <div style={styles.tabs}>
                    <div style={styles.tab(activeTab === 'import')} onClick={() => setActiveTab('import')}>
                        V4 Legacy Import (Zip)
                    </div>
                    <div style={styles.tab(activeTab === 'backup')} onClick={() => setActiveTab('backup')}>
                        V4 Legacy Backup (Export)
                    </div>
                </div>

                {error && <div style={styles.alert('error')}>{error}</div>}
                {message && <div style={styles.alert('success')}>{message}</div>}

                {loading ? (
                    <div>Loading...</div>
                ) : (
                    <div>
                        {activeTab === 'import' && (
                            <div style={styles.actionArea}>
                                <h3>Import Legacy ZIP</h3>
                                <p style={{ color: '#666', marginBottom: '15px' }}>
                                    Upload a ZIP file containing <code>Plots_*.csv</code>, <code>Rois_*.csv</code> etc.
                                </p>
                                <label style={styles.button}>
                                    Select Legacy Backup Zip
                                    <input
                                        type="file"
                                        hidden
                                        accept=".zip"
                                        onChange={handleLegacyImport}
                                    />
                                </label>
                            </div>
                        )}

                        {activeTab === 'backup' && (
                            <div style={styles.actionArea}>
                                <h3>Export Legacy Format</h3>
                                <p style={{ color: '#666', marginBottom: '15px' }}>
                                    Generates a ZIP with files named <code>Plots_Name_Hash.csv</code> strictly matching Legacy Schema.
                                </p>
                                <button
                                    style={styles.button}
                                    onClick={handleLegacyBackup}
                                >
                                    Generate Legacy Backup
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default UnderConstructionPage;
